/**
 * Drives the trivia fight: asks the server for questions, applies damage to Zuk on a correct
 * answer, and reports the outcome when the run ends.
 *
 * The server does the grading and owns the clock, so nothing here decides a score. This
 * module only learns "was that right" and translates it into 10% of Zuk's health.
 */
import { EntityNames, Projectile, Trainer, type Player, type World } from "osrs-sdk";
import type { InfernoRegion } from "../inferno/js/InfernoRegion";
import type { TzKalZuk } from "../inferno/js/mobs/TzKalZuk";
import { makeZukHitsLethal, TRIVIA_HIT } from "./patches";
import { Chatbox } from "./Chatbox";
import {
  API,
  CORRECT_TO_KILL,
  DAMAGE_PER_CORRECT,
  ENRAGE_WHEN_REMAINING,
  ZUK_MAX_HP,
  type Letter,
  type Question,
} from "./constants";

interface StartResponse {
  /** "live" carries a question; "over" means the cookie's run already finished. */
  state: "live" | "over";
  question?: Question;
  correctCount?: number;
  wrongCount?: number;
  killed?: boolean;
  correctAnswers?: number;
  wrongAnswers?: number;
  timeTaken?: number;
}
interface AnswerResponse {
  correct: boolean;
  correctLetter: Letter;
  correctCount: number;
  wrongCount: number;
  next: Question | null;
}
interface EndResponse {
  killed: boolean;
  correctAnswers: number;
  wrongAnswers: number;
  timeTaken: number;
}

type Outcome = "killed" | "died";

async function postJson<T>(url: string, body: unknown = {}): Promise<T> {
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  return res.json() as Promise<T>;
}

class TriviaController {
  private chatbox = new Chatbox();
  private zuk: TzKalZuk | null = null;
  private correct = 0;
  private wrong = 0;
  private enraged = false;
  private over = false;

  constructor(
    private world: World,
    private region: InfernoRegion,
    private player: Player,
  ) {}

  async begin(): Promise<void> {
    this.chatbox.setStatus(0, 0, CORRECT_TO_KILL);
    this.chatbox.onAnswer = (letter) => void this.answer(letter);

    this.hookPlayerDeath();
    this.watchForZukDeath();

    try {
      const res = await postJson<StartResponse>(API.start);

      // A refresh after the fight ended lands here: the cookie's run is over but not yet
      // submitted, so the server keeps it rather than handing out a fresh one. Show the
      // outcome instead of a question — the wrapper page owns the submit form.
      if (res.state === "over") {
        this.over = true;
        const summary = `${res.correctAnswers ?? 0} correct, ${res.wrongAnswers ?? 0} wrong, ${res.timeTaken ?? 0}s.`;
        this.chatbox.finish(
          res.killed ? `TzKal-Zuk falls! ${summary}` : `You died. ${summary}`,
          !!res.killed,
        );
        return;
      }

      this.correct = res.correctCount ?? 0;
      this.wrong = res.wrongCount ?? 0;
      this.chatbox.setStatus(this.correct, this.wrong, CORRECT_TO_KILL);
      // A refresh rebuilds the arena from scratch, so Zuk comes back at full health while the
      // server still remembers the answers already banked. Without this the two disagree and
      // the tenth correct answer leaves him standing with nothing left to ask.
      this.syncZukHealth();
      // Same reason: the enrage flag lives on the mob this page load just created.
      this.maybeEnrage();
      if (res.question) this.chatbox.showQuestion(res.question);
    } catch (err) {
      console.error("Could not start the trivia run", err);
      this.chatbox.say("Could not reach the server. Reload to try again.");
    }
  }

  /** Zuk is in `newMobs` for his first tick, so look in both lists. */
  private findZuk(): TzKalZuk | null {
    if (this.zuk) return this.zuk;
    const all = [...this.region.mobs, ...this.region.newMobs];
    const found = all.find((mob) => mob.mobName() === EntityNames.TZ_KAL_ZUK);
    if (found) {
      this.zuk = found as TzKalZuk;
      makeZukHitsLethal(this.zuk);
    }
    return this.zuk;
  }

  private async answer(letter: Letter): Promise<void> {
    if (this.over) return;
    let result: AnswerResponse;
    try {
      result = await postJson<AnswerResponse>(API.answer, { selected: letter });
    } catch (err) {
      console.error("Answer rejected", err);
      this.chatbox.say("The server rejected that answer. Reload to try again.");
      return;
    }

    this.correct = result.correctCount;
    this.wrong = result.wrongCount;

    if (result.correct) this.damageZuk();
    this.maybeEnrage();

    this.chatbox.reveal(letter, result.correctLetter, DAMAGE_PER_CORRECT);
    this.chatbox.setStatus(this.correct, this.wrong, CORRECT_TO_KILL);

    // A wrong answer costs nothing but the score; the next question lands either way.
    if (result.next && !this.over) {
      window.setTimeout(() => {
        if (!this.over) this.chatbox.showQuestion(result.next as Question);
      }, 900);
    }

    // No next question means the server has counted the tenth correct answer. Normally the
    // damage projectile lands and watchForZukDeath closes the run; this is the backstop for
    // any drift between his on-screen health and the server's tally. end() is idempotent.
    if (!result.next) {
      window.setTimeout(() => void this.end("killed"), 1400);
    }
  }

  /**
   * Deal 10% of Zuk's health, as a real projectile rather than by writing to hitpoint
   * directly, so the engine draws a proper hitsplat and dents the health bar. Zuk's
   * addProjectile patch drops every other player-sourced projectile, so this marker is what
   * distinguishes a trivia answer from the tbow the loadout happens to include.
   *
   * Projectile clamps damage to the target's current health, so the tenth hit lands for
   * exactly what is left. Death is detected by the engine on the following tick.
   */
  damageZuk(): void {
    const zuk = this.findZuk();
    if (!zuk) return;

    const projectile = new Projectile(null, DAMAGE_PER_CORRECT, this.player, zuk, "typeless", {
      hidden: true,
      setDelay: 1,
    });
    (projectile as unknown as Record<string, unknown>)[TRIVIA_HIT] = true;
    zuk.addProjectile(projectile);
  }

  /** Put Zuk's health where the server's answer count says it should be. */
  private syncZukHealth(): void {
    const zuk = this.findZuk();
    if (!zuk) return;
    const expected = Math.max(0, ZUK_MAX_HP - this.correct * DAMAGE_PER_CORRECT);
    if (zuk.currentStats.hitpoint !== expected) {
      zuk.currentStats.hitpoint = expected;
    }
  }

  /**
   * Enrage Zuk for the closing stretch. The flag only feeds his attackSpeed getter (10 ticks
   * -> 7), so flipping it is enough; it takes effect from his next attack. Nothing in the
   * engine signals it visually, hence the banner.
   */
  private maybeEnrage(): void {
    if (this.enraged) return;
    if (CORRECT_TO_KILL - this.correct > ENRAGE_WHEN_REMAINING) return;

    const zuk = this.findZuk();
    if (!zuk) return;

    zuk.enraged = true;
    this.enraged = true;
    this.chatbox.setNotice('TzKal-Zuk is enraged — he attacks faster now.');
  }

  /**
   * Region.onGameOver() fires once, when the last player is removed — and InfernoRegion
   * never overrides it, so it is a free hook.
   */
  private hookPlayerDeath(): void {
    const region = this.region as unknown as { onGameOver: () => void };
    const original = region.onGameOver.bind(region);
    region.onGameOver = () => {
      void this.end("died");
      original();
    };
  }

  /** Poll Zuk's health once a tick by wrapping the region's existing postTick. */
  private watchForZukDeath(): void {
    const region = this.region as unknown as { postTick: () => void };
    const original = region.postTick.bind(region);
    region.postTick = () => {
      original();
      if (this.over) return;
      const zuk = this.findZuk();
      if (zuk && zuk.currentStats.hitpoint <= 0) void this.end("killed");
    };
  }

  private async end(outcome: Outcome): Promise<void> {
    if (this.over) return;
    this.over = true;

    let result: EndResponse;
    try {
      result = await postJson<EndResponse>(API.end, { outcome });
    } catch (err) {
      console.error("Could not report the run", err);
      this.chatbox.finish("Could not save your run. Reload to try again.", false);
      return;
    }

    const summary = `${result.correctAnswers} correct, ${result.wrongAnswers} wrong, ${result.timeTaken}s.`;
    this.chatbox.finish(
      result.killed ? `TzKal-Zuk falls! ${summary}` : `You died. ${summary}`,
      result.killed,
    );

    // The wrapper page owns the results form and the leaderboard.
    window.parent?.postMessage({ type: "inferno-run-over", ...result }, window.location.origin);
  }
}

export function startTrivia(world: World, region: InfernoRegion, player: Player): void {
  const controller = new TriviaController(world, region, player ?? Trainer.player);

  // Test hook. Everything that decides a score is server-side, so this exposes no advantage
  // a devtools console wouldn't already give; it is gated purely to keep it out of the way.
  if (new URLSearchParams(window.location.search).has("debug")) {
    (window as unknown as { __inferno: unknown }).__inferno = {
      world,
      region,
      get player() {
        return Trainer.player;
      },
      get zuk() {
        return [...region.mobs, ...region.newMobs].find(
          (m) => m.mobName() === EntityNames.TZ_KAL_ZUK,
        );
      },
      get mobs() {
        return [...region.mobs, ...region.newMobs].map((m) => m.mobName());
      },
      controller,
    };
  }

  void controller.begin();
}
